import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getUser } from '../../../redux/usersRedux';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReplayIcon from '@mui/icons-material/Replay';
import SendIcon from '@mui/icons-material/Send';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

import {
  finalizeProductionHandover,
  getProductionHandovers,
  requestProductionHandover,
  retryProductionHandover,
  reviewProductionHandover,
} from '../../../api/productionHandoverApi';
import AddIcon from '@mui/icons-material/Add';

import CreateProductionHandoverDialog from './Components/CreateProductionHandoverDialog';
import { productionHandoverStatusLabels } from '../../../utils/productionHandoverLabels';
import ProductionHandoverDetailsDialog from './Components/ProductionHandoverDetailsDialog';
import { io } from 'socket.io-client';
import { SOCKET_URL } from '../../../config';

const socket = io(SOCKET_URL);

const emptyFilters = {
  status: '',
  productionOrder: '',
  batch: '',
  materialNumber: '',
  dateFrom: '',
  dateTo: '',
};

const formatDate = (value) => {
  if (!value) {
    return '-';
  }

  return new Intl.DateTimeFormat('pl-PL', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
};

const formatNumber = (value) => {
  if (value === null || value === undefined) {
    return '-';
  }

  return new Intl.NumberFormat('pl-PL', {
    maximumFractionDigits: 3,
  }).format(value);
};

const getStatusChipColor = (status) => {
  switch (status) {
    case 'AVAILABLE':
      return 'default';

    case 'REQUESTED':
      return 'info';

    case 'PROCESSING':
      return 'primary';

    case 'DATA_COLLECTED':
      return 'warning';

    case 'COMPLETED':
      return 'success';

    case 'DISCREPANCY':
      return 'error';

    case 'FAILED':
      return 'error';

    case 'CLOSED_WITH_DISCREPANCY':
      return 'warning';

    default:
      return 'default';
  }
};

const productionHandoverActionRoles = [
  'ADMIN',
  'KONTROLER',
  'STARSZY_MAGAZYNIER',
];

const ProductionHandoverPage = () => {
  const user = useSelector((state) => getUser(state));

  const [reviewDialog, setReviewDialog] = useState({
    open: false,
    item: null,
    decision: null,
  });

  const [reviewComment, setReviewComment] = useState('');

  const [reviewError, setReviewError] = useState('');
  const [filters, setFilters] = useState(emptyFilters);

  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [items, setItems] = useState([]);

  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(false);

  const [actionLoadingId, setActionLoadingId] = useState(null);

  const [error, setError] = useState(null);

  const [detailsOpen, setDetailsOpen] = useState(false);

  const [selectedHandoverId, setSelectedHandoverId] = useState(null);

  const [pendingAction, setPendingAction] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  const openReviewDialog = (item, decision) => {
    setReviewDialog({
      open: true,
      item,
      decision,
    });

    setReviewComment('');
    setReviewError('');
  };

  const closeReviewDialog = () => {
    if (actionLoadingId) {
      return;
    }

    setReviewDialog({
      open: false,
      item: null,
      decision: null,
    });

    setReviewComment('');
    setReviewError('');
  };

  const executeReviewDecision = async () => {
    const item = reviewDialog.item;
    const decision = reviewDialog.decision;
    const normalizedComment = reviewComment.trim();

    if (!item?.id || !decision) {
      return;
    }

    if (normalizedComment.length < 10) {
      setReviewError('Komentarz musi zawierać co najmniej 10 znaków.');

      return;
    }

    setActionLoadingId(item.id);
    setReviewError('');

    try {
      await reviewProductionHandover(item.id, decision, normalizedComment);

      const message =
        decision === 'RETRY_ANALYSIS'
          ? `Zlecenie ${item.productionOrder} zostało przekazane do ponownej analizy.`
          : `Zlecenie ${item.productionOrder} zostało zamknięte z niezgodnością.`;

      setNotification({
        open: true,
        severity: 'success',
        message,
      });

      setReviewDialog({
        open: false,
        item: null,
        decision: null,
      });

      setReviewComment('');
      setReviewError('');
      await loadData();
    } catch (reviewRequestError) {
      console.error(reviewRequestError);

      const responseMessage = reviewRequestError?.response?.data?.message;

      setReviewError(
        Array.isArray(responseMessage)
          ? responseMessage.join(', ')
          : responseMessage || 'Nie udało się zapisać decyzji.',
      );
    } finally {
      setActionLoadingId(null);
    }
  };

  const openActionConfirmation = (type, item) => {
    setPendingAction({
      type,
      item,
    });
  };

  const closeActionConfirmation = () => {
    if (actionLoadingId) {
      return;
    }

    setPendingAction(null);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await getProductionHandovers({
        ...appliedFilters,
        page: page + 1,
        limit: rowsPerPage,
      });

      setItems(Array.isArray(result.items) ? result.items : []);

      setTotal(result.pagination?.total || 0);
    } catch (loadError) {
      console.error(loadError);

      setError('Nie udało się pobrać listy przekazań produkcji.');
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page, rowsPerPage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const handleChanged = (eventData) => {
      console.log('Zmiana przekazania produkcji:', eventData);

      const isAdmin = user?.users?.role === 'ADMIN';

      const isDifferentWarehouse =
        eventData?.warehouse &&
        user?.users?.warehouse &&
        eventData.warehouse !== user.users.warehouse;

      if (!isAdmin && isDifferentWarehouse) {
        return;
      }

      loadData();
    };

    socket.on('productionHandoverChanged', handleChanged);

    return () => {
      socket.off('productionHandoverChanged', handleChanged);
    };
  }, [loadData, user?.users?.role, user?.users?.warehouse]);

  const handleFilterChange = (field, value) => {
    setFilters((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleSearch = () => {
    setPage(0);
    setAppliedFilters({
      ...filters,
    });
  };

  const handleClearFilters = () => {
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setPage(0);
  };

  const executePendingAction = async () => {
    if (!pendingAction?.item?.id) {
      return;
    }

    const { type, item } = pendingAction;

    setActionLoadingId(item.id);
    setError(null);

    try {
      if (type === 'REQUEST') {
        await requestProductionHandover(item.id);

        setNotification({
          open: true,
          severity: 'success',
          message: `Zlecono analizę zlecenia ${item.productionOrder}.`,
        });
      }

      if (type === 'RETRY') {
        await retryProductionHandover(item.id);

        setNotification({
          open: true,
          severity: 'success',
          message: `Ponowiono analizę zlecenia ${item.productionOrder}.`,
        });
      }

      if (type === 'FINALIZE') {
        await finalizeProductionHandover(item.id);

        setNotification({
          open: true,
          severity: 'success',
          message: `Zlecenie ${item.productionOrder} zostało sfinalizowane.`,
        });
      }

      setPendingAction(null);
      await loadData();
    } catch (actionError) {
      console.error(actionError);

      const responseMessage = actionError?.response?.data?.message;

      setNotification({
        open: true,
        severity: 'error',
        message: Array.isArray(responseMessage)
          ? responseMessage.join(', ')
          : responseMessage || 'Nie udało się wykonać operacji.',
      });
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleOpenDetails = (item) => {
    setSelectedHandoverId(item.id);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedHandoverId(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));

    setPage(0);
  };

  const canManageProductionHandover = productionHandoverActionRoles.includes(
    user?.users?.role,
  );

  const getActionDialogTitle = () => {
    switch (pendingAction?.type) {
      case 'REQUEST':
        return 'Zlecić analizę?';

      case 'RETRY':
        return 'Ponowić analizę?';

      case 'FINALIZE':
        return 'Ponowić finalizację?';

      default:
        return 'Potwierdź operację';
    }
  };

  const getActionDialogText = () => {
    const productionOrder = pendingAction?.item?.productionOrder;

    switch (pendingAction?.type) {
      case 'REQUEST':
        return (
          `Zlecenie ${productionOrder} zostanie ` +
          'przekazane do analizy przez bota.'
        );

      case 'RETRY':
        return (
          `Zlecenie ${productionOrder} zostanie ` +
          'ponownie przekazane do analizy przez bota.'
        );

      case 'FINALIZE':
        return (
          `System ponowi finalizację zlecenia ` +
          `${productionOrder} i wysłanie wiadomości e-mail.`
        );

      default:
        return '';
    }
  };

  const handleProductionHandoverCreated = async (createdHandover) => {
    setCreateDialogOpen(false);

    setNotification({
      open: true,
      severity: 'success',
      message: `Dodano zlecenie ${createdHandover.productionOrder}.`,
    });

    if (page === 0) {
      await loadData();
    } else {
      /*
       * Zmiana strony uruchomi useEffect,
       * który sam wywoła loadData().
       */
      setPage(0);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{
          xs: 'column',
          md: 'row',
        }}
        justifyContent="space-between"
        alignItems={{
          xs: 'flex-start',
          md: 'center',
        }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Typography variant="h5">Przekazanie produkcji</Typography>

        <Stack direction="row" spacing={1}>
          {canManageProductionHandover && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Dodaj zlecenie
            </Button>
          )}

          <Tooltip title="Odśwież">
            <span>
              <IconButton onClick={loadData} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 2,
        }}
      >
        <Stack
          direction={{
            xs: 'column',
            md: 'row',
          }}
          spacing={2}
          useFlexGap
          flexWrap="wrap"
          alignItems="center"
        >
          <TextField
            label="Zlecenie produkcyjne"
            value={filters.productionOrder}
            onChange={(event) =>
              handleFilterChange('productionOrder', event.target.value)
            }
            size="small"
          />

          <TextField
            label="Partia"
            value={filters.batch}
            onChange={(event) =>
              handleFilterChange('batch', event.target.value)
            }
            size="small"
          />

          <TextField
            label="Numer materiału"
            type="number"
            value={filters.materialNumber}
            onChange={(event) =>
              handleFilterChange('materialNumber', event.target.value)
            }
            size="small"
          />

          <FormControl
            size="small"
            sx={{
              minWidth: 220,
            }}
          >
            <InputLabel>Status</InputLabel>

            <Select
              label="Status"
              value={filters.status}
              onChange={(event) =>
                handleFilterChange('status', event.target.value)
              }
            >
              <MenuItem value="">Wszystkie</MenuItem>

              {Object.entries(productionHandoverStatusLabels).map(
                ([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>

          <TextField
            label="Data od"
            type="date"
            value={filters.dateFrom}
            onChange={(event) =>
              handleFilterChange('dateFrom', event.target.value)
            }
            InputLabelProps={{
              shrink: true,
            }}
            size="small"
            sx={{
              width: 190,
            }}
          />

          <TextField
            label="Data do"
            type="date"
            value={filters.dateTo}
            onChange={(event) =>
              handleFilterChange('dateTo', event.target.value)
            }
            InputLabelProps={{
              shrink: true,
            }}
            size="small"
            sx={{
              width: 190,
            }}
          />

          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleSearch}
          >
            Szukaj
          </Button>

          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={handleClearFilters}
          >
            Wyczyść
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={2}>
        {loading ? (
          <Box
            sx={{
              minHeight: 250,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Zlecenie</TableCell>

                    <TableCell>Materiał</TableCell>

                    <TableCell>Partia</TableCell>

                    <TableCell>Linia</TableCell>

                    <TableCell>Status</TableCell>

                    <TableCell align="right">HU produkcja</TableCell>

                    <TableCell align="right">HU EWM</TableCell>

                    <TableCell align="right">Niezgodności</TableCell>

                    <TableCell>Data wyniku</TableCell>

                    <TableCell align="right">Akcje</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {items.map((item) => {
                    const actionLoading = actionLoadingId === item.id;

                    return (
                      <TableRow key={item.id} hover>
                        <TableCell>{item.productionOrder}</TableCell>

                        <TableCell>
                          <Typography variant="body2">
                            {item.materialNumberSnapshot ?? '-'}
                          </Typography>

                          <Typography variant="caption" color="text.secondary">
                            {item.productNameSnapshot || ''}
                          </Typography>
                        </TableCell>

                        <TableCell>{item.batch || '-'}</TableCell>

                        <TableCell>{item.productionLine || '-'}</TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            label={
                              productionHandoverStatusLabels[item.status] ||
                              item.status
                            }
                            color={getStatusChipColor(item.status)}
                          />
                        </TableCell>

                        <TableCell align="right">
                          {formatNumber(item.productionHuCount)}
                        </TableCell>

                        <TableCell align="right">
                          {formatNumber(item.ewmConfirmedHuCount)}
                        </TableCell>

                        <TableCell align="right">
                          {formatNumber(item.discrepancyHuCount)}
                        </TableCell>

                        <TableCell>
                          {formatDate(item.resultGeneratedAt)}
                        </TableCell>

                        <TableCell align="right">
                          <Stack
                            direction="row"
                            justifyContent="flex-end"
                            spacing={0.5}
                          >
                            <Tooltip title="Szczegóły">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDetails(item)}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>

                            {canManageProductionHandover &&
                              item.status === 'AVAILABLE' && (
                                <Tooltip title="Zleć analizę">
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={actionLoading}
                                      onClick={() =>
                                        openActionConfirmation('REQUEST', item)
                                      }
                                    >
                                      <PlayArrowIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}

                            {canManageProductionHandover &&
                              item.status === 'FAILED' && (
                                <Tooltip title="Ponów analizę">
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={actionLoading}
                                      onClick={() =>
                                        openActionConfirmation('RETRY', item)
                                      }
                                    >
                                      <ReplayIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                            {canManageProductionHandover &&
                              item.status === 'DISCREPANCY' && (
                                <>
                                  <Tooltip title="Wyjaśniono – ponów analizę">
                                    <span>
                                      <IconButton
                                        size="small"
                                        disabled={actionLoading}
                                        onClick={() =>
                                          openReviewDialog(
                                            item,
                                            'RETRY_ANALYSIS',
                                          )
                                        }
                                      >
                                        <ReplayIcon />
                                      </IconButton>
                                    </span>
                                  </Tooltip>

                                  <Tooltip title="Zamknij z niezgodnością">
                                    <span>
                                      <IconButton
                                        size="small"
                                        color="warning"
                                        disabled={actionLoading}
                                        onClick={() =>
                                          openReviewDialog(
                                            item,
                                            'CLOSE_WITH_DISCREPANCY',
                                          )
                                        }
                                      >
                                        <WarningAmberIcon />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </>
                              )}

                            {canManageProductionHandover &&
                              item.status === 'DATA_COLLECTED' && (
                                <Tooltip title="Ponów finalizację">
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={actionLoading}
                                      onClick={() =>
                                        openActionConfirmation('FINALIZE', item)
                                      }
                                    >
                                      <SendIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                        Brak przekazań spełniających kryteria.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={total}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Wierszy na stronie:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} z ${count !== -1 ? count : `więcej niż ${to}`}`
              }
            />
          </>
        )}
      </Paper>
      <ProductionHandoverDetailsDialog
        open={detailsOpen}
        handoverId={selectedHandoverId}
        onClose={handleCloseDetails}
      />
      <CreateProductionHandoverDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={handleProductionHandoverCreated}
      />
      <Dialog
        open={Boolean(pendingAction)}
        onClose={closeActionConfirmation}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{getActionDialogTitle()}</DialogTitle>

        <DialogContent>
          <DialogContentText>{getActionDialogText()}</DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={closeActionConfirmation}
            disabled={Boolean(actionLoadingId)}
          >
            Anuluj
          </Button>

          <Button
            variant="contained"
            onClick={executePendingAction}
            disabled={Boolean(actionLoadingId)}
            startIcon={
              actionLoadingId ? (
                <CircularProgress size={18} color="inherit" />
              ) : null
            }
          >
            Potwierdź
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={reviewDialog.open}
        onClose={closeReviewDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {reviewDialog.decision === 'RETRY_ANALYSIS'
            ? 'Wyjaśniono niezgodność'
            : 'Zamknij z niezgodnością'}
        </DialogTitle>

        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {reviewDialog.decision === 'RETRY_ANALYSIS'
              ? `Opisz, co zostało wyjaśnione lub poprawione dla zlecenia ${reviewDialog.item?.productionOrder}. Po zatwierdzeniu bot wykona pełną analizę ponownie.`
              : `Opisz, dlaczego zlecenie ${reviewDialog.item?.productionOrder} zostaje zamknięte mimo nierozwiązanej niezgodności.`}
          </DialogContentText>

          {reviewError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {reviewError}
            </Alert>
          )}

          <TextField
            label="Komentarz"
            value={reviewComment}
            onChange={(event) => {
              setReviewComment(event.target.value);

              if (reviewError) {
                setReviewError('');
              }
            }}
            multiline
            minRows={4}
            fullWidth
            required
            error={reviewComment.length > 0 && reviewComment.trim().length < 10}
            helperText={
              'Minimum 10 znaków. Komentarz zostanie zapisany w historii procesu.'
            }
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={closeReviewDialog}
            disabled={Boolean(actionLoadingId)}
          >
            Anuluj
          </Button>

          <Button
            variant="contained"
            color={
              reviewDialog.decision === 'CLOSE_WITH_DISCREPANCY'
                ? 'warning'
                : 'primary'
            }
            onClick={executeReviewDecision}
            disabled={
              Boolean(actionLoadingId) || reviewComment.trim().length < 10
            }
            startIcon={
              actionLoadingId ? (
                <CircularProgress size={18} color="inherit" />
              ) : null
            }
          >
            {reviewDialog.decision === 'RETRY_ANALYSIS'
              ? 'Ponów analizę'
              : 'Zamknij z niezgodnością'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={() =>
          setNotification((previous) => ({
            ...previous,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <Alert
          severity={notification.severity}
          variant="filled"
          onClose={() =>
            setNotification((previous) => ({
              ...previous,
              open: false,
            }))
          }
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductionHandoverPage;
